import type { RepositoryFailure, RepositoryResult } from "../contracts";
import type {
  ActionCommand, ActivateOnboardingWorkflowCommand, ApprovePromptSetCommand, ApproveReportVersionV1Command, AyzeoImportCommand, ClientAuditCommand, CreateReportReplacementV1Command, ReviewReportVersionV1Command, SaveAyzeoPdfSnapshotV1Command, SaveClientDetailsV1Command, SaveOnboardingWorkflowV1Command, SaveReportDraftV1Command,
  CreateEvidenceRecordCommand,
  DecisionCommand, DeliveryCommand, EntityReviewCommand, InitialiseReportCommand,
  LimitationCommand, PdfCommand, PromptSetCommand,
} from "../a2b-commands";

export type PersistenceAuthorityKind = "Local" | "SupabaseLocalRehearsal";
export type CutoverState =
  | "LocalAuthoritative" | "Staging" | "Staged" | "ParityVerified"
  | "SupabaseRehearsalAuthoritative" | "Diverged" | "RolledBack" | "Failed";
export type DivergenceState = "None" | "SupabaseAhead";

export interface AuthorityDescriptor {
  authority: PersistenceAuthorityKind;
  workspaceId: string;
  workspaceKind: "synthetic-cutover" | "operational" | "demonstration" | "legacy-unclassified";
  organisationId?: string;
  auditId?: string;
  actorId?: string;
  activationState: CutoverState;
  sourceFingerprint: string;
  stagedTargetFingerprint?: string;
  divergence: DivergenceState;
  rollbackEligible: boolean;
}

export type AuthenticatedSessionState =
  | { status: "signed_out" | "authenticating" | "unavailable"; message?: string }
  | { status: "expired"; actorId?: string; message?: string }
  | { status: "insufficient_assurance"; actorId: string; currentLevel: "aal1"; nextLevel?: "aal1" | "aal2"; message: string }
  | { status: "authenticated"; actorId: string; role?: string; currentLevel: "aal2"; nextLevel?: "aal2" };

export type GatewayFailureKind =
  | "unauthenticated" | "insufficient_assurance" | "expired_session" | "unauthorised" | "unavailable" | "not_found"
  | "validation_failure" | "stale_revision" | "idempotency_conflict"
  | "immutable_state_violation" | "transient_failure" | "unexpected_persistence_failure"
  | "unsupported";

export type GatewayReadResult<T> =
  | { status: "loading" }
  | { status: "ready"; value: T; authority: PersistenceAuthorityKind }
  | { status: GatewayFailureKind; message: string; retryable: boolean; authority: PersistenceAuthorityKind };

export interface GatewayCommandSuccess<T> {
  status: "committed" | "idempotent_replay";
  value: T;
  authority: PersistenceAuthorityKind;
  authoritativeMutation: boolean;
  refetchRequired: boolean;
}
export interface GatewayCommandFailure {
  status: GatewayFailureKind;
  message: string;
  retryable: boolean;
  authority: PersistenceAuthorityKind;
  authoritativeMutation: false;
  refetchRequired: boolean;
}
export type GatewayCommandResult<T> = GatewayCommandSuccess<T> | GatewayCommandFailure;

export type GatewayCommand =
  | { kind: "createClientAudit"; value: ClientAuditCommand }
  | { kind: "saveClientDetailsV1"; value: SaveClientDetailsV1Command }
  | { kind: "activateOnboardingWorkflow"; value: ActivateOnboardingWorkflowCommand }
  | { kind: "saveOnboardingWorkflowV1"; value: SaveOnboardingWorkflowV1Command }
  | { kind: "saveReportDraftV1"; value: SaveReportDraftV1Command }
  | { kind: "reviewReportVersionV1"; value: ReviewReportVersionV1Command }
  | { kind: "approveReportVersionV1"; value: ApproveReportVersionV1Command }
  | { kind: "createReportReplacementV1"; value: CreateReportReplacementV1Command }
  | { kind: "initialiseReport"; value: InitialiseReportCommand }
  | { kind: "createPromptSet"; value: PromptSetCommand }
  | { kind: "approvePromptSet"; value: ApprovePromptSetCommand }
  | { kind: "importCollection"; value: AyzeoImportCommand }
  | { kind: "saveAyzeoPdfSnapshotV1"; value: SaveAyzeoPdfSnapshotV1Command }
  | { kind: "createEvidenceRecord"; value: CreateEvidenceRecordCommand }
  | { kind: "commitEntityReview"; value: EntityReviewCommand }
  | { kind: "linkLimitation"; value: LimitationCommand }
  | { kind: "commitDecision"; value: DecisionCommand }
  | { kind: "createAction"; value: ActionCommand }
  | { kind: "transitionAction"; value: ActionCommand }
  | { kind: "prepareExport"; value: PdfCommand }
  | { kind: "supersedeExport"; value: PdfCommand & { predecessorExportId: string; status: "Superseded" | "Withdrawn" } }
  | { kind: "recordDelivery"; value: DeliveryCommand };

export interface ClientAuditView { organisation?: unknown; contacts: unknown[]; onboarding?: unknown; stages: unknown[]; audit?: unknown; }
export interface PromptPlanningView { platforms: unknown[]; configurations: unknown[]; promptSets: unknown[]; approvedPromptSet?: unknown; prompts: unknown[]; }
export interface RetrievalEvidenceView { projects: unknown[]; batches: unknown[]; executions: unknown[]; reviews: unknown[]; limitations: unknown[]; }
export interface CommercialWorkflowView { decisions: unknown[]; actions: unknown[]; }
export interface ReportDeliveryView { report?: unknown; version?: unknown; sections: unknown[]; exports: unknown[]; eligibleExports: unknown[]; deliveries: unknown[]; }

export interface ApplicationPersistenceAdapter {
  readonly kind: PersistenceAuthorityKind;
  readClientAudit(organisationId: string, auditId: string): Promise<GatewayReadResult<ClientAuditView>>;
  readPromptPlanning(auditId: string): Promise<GatewayReadResult<PromptPlanningView>>;
  readRetrievalEvidence(auditId: string): Promise<GatewayReadResult<RetrievalEvidenceView>>;
  readCommercialWorkflow(auditId: string): Promise<GatewayReadResult<CommercialWorkflowView>>;
  readReportDelivery(reportId: string, versionId: string): Promise<GatewayReadResult<ReportDeliveryView>>;
  execute(command: GatewayCommand): Promise<GatewayCommandResult<Record<string, unknown>>>;
}

export function gatewayFailureFromRepository(
  error: RepositoryFailure,
  authority: PersistenceAuthorityKind,
): GatewayCommandFailure {
  const text = `${error.message}`.toLowerCase();
  const status: GatewayFailureKind = error.code === "not_found" ? "not_found"
    : error.code === "insufficient_assurance" ? "insufficient_assurance"
    : error.code === "permission_denied" ? "unauthorised"
      : error.code === "validation_failure" ? "validation_failure"
        : error.code === "conflict" && (text.includes("immutable") || text.includes("locked")) ? "immutable_state_violation"
          : error.code === "conflict" && text.includes("idempot") ? "idempotency_conflict"
          : error.code === "conflict" && (text.includes("revision") || text.includes("stale")) ? "stale_revision"
          : error.code === "conflict" ? "idempotency_conflict"
            : /fetch failed|failed to fetch|network|econnrefused|connection refused/u.test(text) ? "unavailable"
                : text.includes("immutable") || text.includes("locked") ? "immutable_state_violation"
                : error.retryable ? "transient_failure" : "unexpected_persistence_failure";
  return { status, message: error.message, retryable: error.retryable, authority, authoritativeMutation: false, refetchRequired: status === "stale_revision" || status === "idempotency_conflict" };
}

export function commandResult<T extends Record<string, unknown>>(
  result: RepositoryResult<T>, authority: PersistenceAuthorityKind,
): GatewayCommandResult<T> {
  if (!result.ok) return gatewayFailureFromRepository(result.error, authority);
  const replay = result.value.idempotentReplay === true || result.value.idempotent_replay === true;
  return { status: replay ? "idempotent_replay" : "committed", value: result.value, authority, authoritativeMutation: !replay, refetchRequired: true };
}

export function readResult<T>(result: RepositoryResult<T>, authority: PersistenceAuthorityKind): GatewayReadResult<T> {
  if (result.ok) return { status: "ready", value: result.value, authority };
  const failure = gatewayFailureFromRepository(result.error, authority);
  return { status: failure.status, message: failure.message, retryable: failure.retryable, authority };
}
